'use client';

import { useEffect } from 'react';

import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastProps {
  open: boolean;
  tone?: ToastTone;
  title: string;
  message?: string;
  /** Auto-dismiss after this many ms. Pass 0 to keep it until closed. */
  durationMs?: number;
  onClose: () => void;
}

const TONE_STYLES: Record<ToastTone, string> = {
  success: 'border-success/50 bg-success/15 text-success',
  error: 'border-danger/50 bg-danger/15 text-danger',
  info: 'border-brand/50 bg-brand/15 text-brand-bright',
};

/**
 * Fixed corner toast — used after modal actions (add/claim tablet) so success
 * feedback stays visible after the dialog closes.
 */
export function Toast({
  open,
  tone = 'success',
  title,
  message,
  durationMs = 4000,
  onClose,
}: ToastProps) {
  useEffect(() => {
    if (!open || durationMs <= 0) return;
    const timer = window.setTimeout(onClose, durationMs);
    return () => window.clearTimeout(timer);
  }, [open, durationMs, onClose]);

  if (!open) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-20 z-[90] flex justify-center px-4 sm:inset-x-auto sm:right-6 sm:justify-end"
    >
      <div
        className={cn(
          'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-2xl border px-4 py-3 shadow-[0_20px_50px_-25px_rgb(0_0_0/0.8)] backdrop-blur-md',
          TONE_STYLES[tone],
        )}
      >
        <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-current/10">
          <Icon name={tone === 'error' ? 'close' : 'check'} className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">{title}</p>
          {message ? <p className="text-content-muted mt-0.5 text-sm leading-relaxed">{message}</p> : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-content-muted hover:text-white rounded-lg p-1 transition"
          aria-label="Dismiss notification"
        >
          <Icon name="close" className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
